import React from 'react';
import { Plus, Trash2, Briefcase, ShieldAlert } from 'lucide-react';
import { cn } from '../../lib/utils';
import { LeadsStore } from '../../lib/leadsStore';
import AddressAutocomplete from '../forms/AddressAutocomplete';
import { Group, Field, Select, Toggle, DateField } from './atoms';
import { money } from './helpers';

/* =============== Profile ============== */

export function ProfileSection({ data, set, setMany, flushSave, dense }) {
  // Inverse of the PropertySection's "Same as current" toggle. Useful when the
  // LO already filled out the subject property (common for refis & purchases
  // where the borrower will live in the home) and wants to mirror it back
  // into the borrower's current-residence block.
  const hasSubjectAddress = !!(data.propertyAddress || data.propertyCity || data.propertyZip);
  const sameAsSubject = hasSubjectAddress &&
    data.currentAddress === data.propertyAddress &&
    data.currentCity === data.propertyCity &&
    data.currentState === data.propertyState &&
    data.currentZip === data.propertyZip;

  const applySubjectAddress = () => {
    const patch = {
      currentAddress: data.propertyAddress || '',
      currentUnit: data.propertyUnit || '',
      currentCity: data.propertyCity || '',
      currentState: data.propertyState || '',
      currentCounty: data.propertyCounty || '',
      currentZip: data.propertyZip || '',
    };
    if (typeof setMany === 'function') setMany(patch);
    else Object.entries(patch).forEach(([k, v]) => set(k, v));
    // Toggles are explicit, one-shot actions — bypass the 800ms debounce
    // so the LO can immediately refresh the page (or close the tab) without
    // losing the write. Without this, clicking "Same as subject property"
    // and then hitting browser-refresh inside the debounce window would
    // silently revert the address.
    if (typeof flushSave === 'function') {
      // Defer to next tick so React commits `setMany` first; otherwise
      // flushSave would persist the *pre-toggle* `data`.
      setTimeout(() => flushSave(), 0);
    }
  };

  const clearCurrentAddress = () => {
    const patch = {
      currentAddress: '',
      currentUnit: '',
      currentCity: '',
      currentState: '',
      currentCounty: '',
      currentZip: '',
    };
    if (typeof setMany === 'function') setMany(patch);
    else Object.entries(patch).forEach(([k, v]) => set(k, v));
    if (typeof flushSave === 'function') {
      setTimeout(() => flushSave(), 0);
    }
  };

  return (
    <div className="space-y-6">
      <Group title="Personal information" dense={dense}>
        <Field label="First name" value={data.firstName} onChange={(v) => set('firstName', v)} required />
        <Field label="Middle" value={data.middleName} onChange={(v) => set('middleName', v)} />
        <Field label="Last name" value={data.lastName} onChange={(v) => set('lastName', v)} required />
        <Field label="Suffix" value={data.suffix} onChange={(v) => set('suffix', v)} />
        <Select label="Marital status" value={data.marital} onChange={(v) => set('marital', v)} options={['Single','Married','Separated','Divorced','Widowed']} />
        <Select label="Citizenship" value={data.citizenship} onChange={(v) => set('citizenship', v)} options={['US Citizen','Permanent Resident','Non-Permanent Resident']} />
      </Group>

      <Group title="Contact" dense={dense}>
        <Field label="Email" value={data.email} onChange={(v) => set('email', v)} required />
        <Field label="Phone" mask="phone" value={data.phone} onChange={(v) => set('phone', v)} placeholder="XXX-XXX-XXXX" required />
      </Group>

      <div>
        <div className="flex items-center justify-between mb-4 pb-2 border-b hairline">
          <div className="text-[14px] font-bold text-neutral-900 dark:text-neutral-100">Current residence</div>
          {hasSubjectAddress && (
            <button
              type="button"
              data-testid="current-same-as-subject"
              onClick={() => (sameAsSubject ? clearCurrentAddress() : applySubjectAddress())}
              title="Copy the subject property address into the borrower's current residence"
              className={cn(
                'flex items-center gap-2 h-8 px-3 rounded-md text-[11.5px] font-semibold transition',
                sameAsSubject
                  ? 'bg-[hsl(var(--kf-gold))] text-neutral-900 ring-1 ring-[hsl(var(--kf-gold-deep))]/40 shadow-[0_0_0_3px_hsl(var(--kf-gold))/0.15]'
                  : 'border hairline bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 hover:border-[hsl(var(--kf-gold))] hover:text-[hsl(var(--kf-gold-deep))]'
              )}
            >
              <span className={cn('inline-block h-3 w-3 rounded-full border-2',
                sameAsSubject ? 'border-neutral-900 bg-white' : 'border-neutral-300 dark:border-neutral-600')} />
              Same as subject property
            </button>
          )}
        </div>
        <div className={cn('grid gap-5',
          dense ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'
        )}>
        <div className={cn(dense ? 'sm:col-span-2' : 'md:col-span-3')}>
          <AddressAutocomplete
            label="Current address"
            required
            testId="current-address"
            value={data.currentAddress}
            onChange={(v) => set('currentAddress', v)}
            onSelect={(d) => {
              set('currentAddress', d.street || data.currentAddress);
              set('currentCity', d.city || '');
              set('currentState', d.state_code || '');
              set('currentCounty', d.county || '');
              set('currentZip', d.zip || '');
            }}
          />
        </div>
        <Field label="Unit / Apt" value={data.currentUnit} onChange={(v) => set('currentUnit', v)} />
        <Field label="City" value={data.currentCity} onChange={(v) => set('currentCity', v)} required />
        <Field label="State" value={data.currentState} onChange={(v) => set('currentState', v.toUpperCase().slice(0, 2))} mono />
        <Field label="ZIP" value={data.currentZip} onChange={(v) => set('currentZip', v)} mono />
        <Field label="County" value={data.currentCounty} onChange={(v) => set('currentCounty', v)} />
        <Field label="Years at residence" type="number" value={data.yearsAtAddress} onChange={(v) => set('yearsAtAddress', Number(v))} />
        <Select label="Occupancy" value={data.livingSituation} onChange={(v) => set('livingSituation', v)} options={['Own','Rent','Live with family']} />
        <Field label="Previous address (if < 2yr)" value={data.prevAddress} onChange={(v) => set('prevAddress', v)} colSpan={4} />
        </div>
      </div>
    </div>
  );
}

export function CoBorrowerSection({ primary, data, set, setMany, dense }) {
  const [sameAddr, setSameAddr] = React.useState(() => (
    !!primary.currentAddress && data.currentAddress === primary.currentAddress
  ));

  const toggleSame = (next) => {
    setSameAddr(next);
    if (next) {
      setMany({
        currentAddress: primary.currentAddress || '',
        currentUnit: primary.currentUnit || '',
        currentCity: primary.currentCity || '',
        currentState: primary.currentState || '',
        currentCounty: primary.currentCounty || '',
        currentZip: primary.currentZip || '',
        yearsAtAddress: primary.yearsAtAddress || 0,
        monthsAtAddress: primary.monthsAtAddress || 0,
        livingSituation: primary.livingSituation || 'Own',
      });
    }
  };

  const setEdit = (field, value) => {
    if (sameAddr) setSameAddr(false);
    set(field, value);
  };

  return (
    <div className="space-y-6">
      <Group title="Personal information" dense={dense}>
        <Field label="First name" value={data.firstName} onChange={(v) => set('firstName', v)} required />
        <Field label="Middle" value={data.middleName} onChange={(v) => set('middleName', v)} />
        <Field label="Last name" value={data.lastName} onChange={(v) => set('lastName', v)} required />
        <Field label="Suffix" value={data.suffix} onChange={(v) => set('suffix', v)} />
        <Select label="Marital status" value={data.marital} onChange={(v) => set('marital', v)} options={['Single','Married','Separated','Divorced','Widowed']} />
        <Select label="Citizenship" value={data.citizenship} onChange={(v) => set('citizenship', v)} options={['US Citizen','Permanent Resident','Non-Permanent Resident']} />
      </Group>

      <Group title="Contact" dense={dense}>
        <Field label="Email" value={data.email} onChange={(v) => set('email', v)} />
        <Field label="Phone" mask="phone" value={data.phone} onChange={(v) => set('phone', v)} placeholder="XXX-XXX-XXXX" />
      </Group>

      <div>
        <div className="flex items-center justify-between mb-4 pb-2 border-b hairline">
          <div className="text-[14px] font-bold text-neutral-900">Current residence</div>
          <button
            type="button"
            data-testid="coborrower-same-address"
            onClick={() => toggleSame(!sameAddr)}
            className={cn(
              'flex items-center gap-2 h-8 px-3 rounded-md text-[11.5px] font-semibold transition',
              sameAddr
                ? 'bg-neutral-900 text-white'
                : 'border hairline bg-white text-neutral-700 hover:border-neutral-400'
            )}
          >
            <span className={cn('inline-block h-3 w-3 rounded-full border-2',
              sameAddr ? 'border-white bg-[hsl(var(--kf-gold))]' : 'border-neutral-300')} />
            Same as primary borrower
          </button>
        </div>
        <div className={cn(
          'grid gap-5 transition-opacity',
          dense ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
          sameAddr && 'opacity-60 pointer-events-none'
        )}>
          <div className={cn(dense ? 'sm:col-span-2' : 'md:col-span-3')}>
            <AddressAutocomplete
              label="Current address"
              testId="co-current-address"
              value={data.currentAddress}
              onChange={(v) => setEdit('currentAddress', v)}
              onSelect={(d) => {
                setEdit('currentAddress', d.street || data.currentAddress);
                setEdit('currentCity', d.city || '');
                setEdit('currentState', d.state_code || '');
                setEdit('currentCounty', d.county || '');
                setEdit('currentZip', d.zip || '');
              }}
            />
          </div>
          <Field label="Unit / Apt" value={data.currentUnit} onChange={(v) => setEdit('currentUnit', v)} />
          <Field label="City" value={data.currentCity} onChange={(v) => setEdit('currentCity', v)} />
          <Field label="State" value={data.currentState} onChange={(v) => setEdit('currentState', (v || '').toUpperCase().slice(0, 2))} mono />
          <Field label="ZIP" value={data.currentZip} onChange={(v) => setEdit('currentZip', v)} mono />
          <Field label="County" value={data.currentCounty} onChange={(v) => setEdit('currentCounty', v)} />
          <Field label="Years at residence" type="number" value={data.yearsAtAddress} onChange={(v) => setEdit('yearsAtAddress', Number(v))} />
          <Select label="Occupancy" value={data.livingSituation} onChange={(v) => setEdit('livingSituation', v)} options={['Own','Rent','Live with family']} />
        </div>
      </div>
    </div>
  );
}

/* =============== Employment ============== */

export function EmploymentBlock({ data, set, totalIncome, dense }) {
  return (
    <>
      <Group title="Employment" dense={dense}>
        <Field label="Employer" value={data.employer} onChange={(v) => set('employer', v)} colSpan={2} required />
        <Field label="Position" value={data.position} onChange={(v) => set('position', v)} required />
        <Select label="Employment type" value={data.employmentType || 'W2 Employed'} onChange={(v) => set('employmentType', v || 'W2 Employed')} options={['W2 Employed','Self Employed','1099 Contractor','Retired','Other']} />
        <DateField label="Start date" value={data.employmentStart} onChange={(v) => set('employmentStart', v)} />
      </Group>

      <Group title="Monthly income" dense={dense}>
        <Field label="Base salary" type="number" value={data.monthlyBaseIncome} onChange={(v) => set('monthlyBaseIncome', Number(v))} prefix="$" mono required />
        <Field label="Overtime" type="number" value={data.monthlyOvertime} onChange={(v) => set('monthlyOvertime', Number(v))} prefix="$" mono />
        <Field label="Bonus" type="number" value={data.monthlyBonus} onChange={(v) => set('monthlyBonus', Number(v))} prefix="$" mono />
        <Field label="Commission" type="number" value={data.monthlyCommission} onChange={(v) => set('monthlyCommission', Number(v))} prefix="$" mono />
        <Field label="Other income" type="number" value={data.monthlyOther} onChange={(v) => set('monthlyOther', Number(v))} prefix="$" mono />
        <Field label="Other source" value={data.otherIncomeSource} onChange={(v) => set('otherIncomeSource', v)} placeholder="Rental, child support, etc." />
      </Group>
      <div className="rounded-xl border hairline bg-[hsl(var(--kf-gold-soft))]/50 px-5 py-4 flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold-deep))] font-bold">Total monthly income</div>
        <div className="display text-[22px] font-bold text-neutral-900 tracking-tight">{money(totalIncome)}</div>
      </div>
    </>
  );
}

// Shared "primary + co-borrower split layout" wrapper used by Employment & Liabilities tabs.
function useLayoutSync() {
  const [layout, setLayout] = React.useState(() => localStorage.getItem('kf_coborrower_layout') || 'stacked');
  React.useEffect(() => {
    const onChange = (e) => setLayout(e?.detail || localStorage.getItem('kf_coborrower_layout') || 'stacked');
    window.addEventListener('kf:layout-change', onChange);
    window.addEventListener('storage', onChange);
    return () => {
      window.removeEventListener('kf:layout-change', onChange);
      window.removeEventListener('storage', onChange);
    };
  }, []);
  return layout;
}

export function EmploymentTab({ lead, data, set, totalIncome, coData, setCoData }) {
  const hasCo = !!lead?.co_borrower;
  const layout = useLayoutSync();
  const co = coData || lead?.co_borrower || {};
  const updateCoField = (field, value) => {
    if (setCoData) {
      setCoData((prev) => ({ ...(prev || {}), [field]: value }));
    } else {
      // Fallback when host hasn't wired the buffer (shouldn't happen in app).
      LeadsStore.update(lead.id, { co_borrower: { ...(lead.co_borrower || {}), [field]: value } });
    }
  };
  const coTotalIncome = (co.monthlyBaseIncome || 0) + (co.monthlyOvertime || 0) + (co.monthlyBonus || 0) + (co.monthlyCommission || 0) + (co.monthlyOther || 0);

  const primaryBlock = (
    <section className="rounded-2xl border hairline bg-white p-6 md:p-7 space-y-6">
      {hasCo && (
        <div className="flex items-center gap-3 pb-4 border-b hairline-soft">
          <div className="h-9 w-9 rounded-full bg-neutral-900 text-[hsl(var(--kf-gold))] flex items-center justify-center">
            <Briefcase className="h-3.5 w-3.5" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Primary borrower</div>
            <div className="display text-[16px] font-bold text-neutral-900 tracking-tight">{data.firstName} {data.lastName}</div>
          </div>
        </div>
      )}
      <EmploymentBlock data={data} set={set} totalIncome={totalIncome} dense={hasCo && layout === 'split'} />
    </section>
  );

  const coBlock = hasCo && (
    <section className="rounded-2xl border hairline bg-white p-6 md:p-7 space-y-6 rise-in">
      <div className="flex items-center gap-3 pb-4 border-b hairline-soft">
        <div className="h-9 w-9 rounded-full bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] flex items-center justify-center border border-[hsl(var(--kf-gold))]/30">
          <Briefcase className="h-3.5 w-3.5" strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold-deep))] font-bold">Co-borrower</div>
          <div className="display text-[16px] font-bold text-neutral-900 tracking-tight">{co.firstName || '—'} {co.lastName || ''}</div>
        </div>
      </div>
      <EmploymentBlock data={co} set={updateCoField} totalIncome={coTotalIncome} dense={layout === 'split'} />
    </section>
  );

  if (!hasCo) return <div className="space-y-6">{primaryBlock}</div>;
  if (layout === 'split') {
    return <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">{primaryBlock}{coBlock}</div>;
  }
  return <div className="space-y-6">{primaryBlock}{coBlock}</div>;
}

/* =============== Credit & Liabilities ============== */

export function CreditBlock({ data, set, dense, testIdPrefix = 'lead' }) {
  return (
    <>
      <Group title="Identity verification" dense={dense}>
        <Field label="Date of birth" mask="date" value={data.dob} onChange={(v) => set('dob', v)} placeholder="MM/DD/YYYY" required testId={`${testIdPrefix}-dob`} />
        <Field label="SSN" mask="ssn" secret value={data.ssn} onChange={(v) => set('ssn', v)} placeholder="XXX-XX-XXXX" required testId={`${testIdPrefix}-ssn`} />
      </Group>

      <Group title="Credit pull" dense={dense}>
        <Field label="Credit score (FICO)" type="number" value={data.creditScore} onChange={(v) => set('creditScore', Number(v))} required />
        <Field label="Date pulled" mask="date" value={data.creditPulled} onChange={(v) => set('creditPulled', v)} placeholder="MM/DD/YYYY" />
      </Group>

      <Group title="Monthly debts" dense={dense}>
        <Field label="Monthly debt total" type="number" value={data.monthlyDebts} onChange={(v) => set('monthlyDebts', Number(v))} prefix="$" mono colSpan={2} />
        <div className="text-[11.5px] text-neutral-500 self-end pb-2">
          Pull-credit will auto-populate liabilities. Edit individual lines after the pull.
        </div>
      </Group>
      <div className="rounded-xl border hairline bg-[hsl(var(--kf-gold-soft))]/50 px-5 py-4 flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold-deep))] font-bold">Total monthly debt obligations</div>
        <div className="display text-[22px] font-bold text-neutral-900 tracking-tight">{money(data.monthlyDebts)}</div>
      </div>
    </>
  );
}

export function LiabilitiesTab({ lead, data, set, coData, setCoData }) {
  const hasCo = !!lead?.co_borrower;
  const layout = useLayoutSync();
  const co = coData || lead?.co_borrower || {};
  const updateCoField = (field, value) => {
    if (setCoData) {
      setCoData((prev) => ({ ...(prev || {}), [field]: value }));
    } else {
      LeadsStore.update(lead.id, { co_borrower: { ...(lead.co_borrower || {}), [field]: value } });
    }
  };

  const combinedDebts = (Number(data.monthlyDebts) || 0) + (Number(co.monthlyDebts) || 0);

  const primaryBlock = (
    <section className="rounded-2xl border hairline bg-white p-6 md:p-7 space-y-6">
      {hasCo && (
        <div className="flex items-center gap-3 pb-4 border-b hairline-soft">
          <div className="h-9 w-9 rounded-full bg-neutral-900 text-[hsl(var(--kf-gold))] flex items-center justify-center">
            <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Primary borrower</div>
            <div className="display text-[16px] font-bold text-neutral-900 tracking-tight">{data.firstName} {data.lastName}</div>
          </div>
        </div>
      )}
      <CreditBlock data={data} set={set} dense={hasCo && layout === 'split'} />
    </section>
  );

  const coBlock = hasCo && (
    <section className="rounded-2xl border hairline bg-white p-6 md:p-7 space-y-6 rise-in">
      <div className="flex items-center gap-3 pb-4 border-b hairline-soft">
        <div className="h-9 w-9 rounded-full bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] flex items-center justify-center border border-[hsl(var(--kf-gold))]/30">
          <ShieldAlert className="h-3.5 w-3.5" strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold-deep))] font-bold">Co-borrower</div>
          <div className="display text-[16px] font-bold text-neutral-900 tracking-tight">{co.firstName || '—'} {co.lastName || ''}</div>
        </div>
      </div>
      <CreditBlock data={co} set={updateCoField} dense={layout === 'split'} testIdPrefix="coborrower" />
    </section>
  );

  const combinedCard = hasCo && (
    <div className="rounded-xl border hairline bg-[hsl(var(--kf-gold-soft))]/50 px-5 py-4 flex items-center justify-between">
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold-deep))] font-bold">Combined monthly debt</div>
      <div className="display text-[22px] font-bold text-neutral-900 tracking-tight">{money(combinedDebts)}</div>
    </div>
  );

  if (!hasCo) return <div className="space-y-6">{primaryBlock}</div>;
  if (layout === 'split') {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 2xl:grid-cols-2 gap-6">{primaryBlock}{coBlock}</div>
        {combinedCard}
      </div>
    );
  }
  return <div className="space-y-6">{primaryBlock}{coBlock}{combinedCard}</div>;
}

/* =============== Assets ============== */

export function AssetsSection({ data, set, totalAssets }) {
  const assets = data.assets || [];
  const update = (i, k, v) => {
    const next = assets.map((a, idx) => idx === i ? { ...a, [k]: v } : a);
    set('assets', next);
  };
  const add = () => set('assets', [...assets, { type: 'Checking', institution: '', balance: 0 }]);
  const remove = (i) => set('assets', assets.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="display text-[16px] font-bold text-neutral-900 tracking-tight">Bank, retirement & other assets</h3>
          <p className="text-[12px] text-neutral-500">Add every account that will be used for cash to close or reserves.</p>
        </div>
        <button onClick={add} className="h-9 px-3 rounded-md border hairline bg-white hover:border-neutral-400 text-[12px] font-semibold text-neutral-700 flex items-center gap-1.5">
          <Plus className="h-4 w-4" /> Add asset
        </button>
      </div>

      <div className="rounded-xl border hairline overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-2.5 bg-neutral-50/60 border-b hairline text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
          <div className="col-span-3">Type</div>
          <div className="col-span-5">Institution</div>
          <div className="col-span-3">Balance</div>
          <div className="col-span-1"></div>
        </div>
        {assets.map((a, i) => (
          <div key={i} className="grid grid-cols-12 gap-3 px-4 py-3 border-b hairline-soft last:border-0 items-center">
            <div className="col-span-3">
              <select value={a.type} onChange={(e) => update(i, 'type', e.target.value)}
                className="w-full h-9 px-2 rounded-md bg-white border hairline text-[13px] focus:outline-none focus:border-neutral-900">
                {['Checking','Savings','Money Market','CD','401(k)','IRA','Brokerage','Gift Funds','Other'].map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div className="col-span-5">
              <input value={a.institution} onChange={(e) => update(i, 'institution', e.target.value)}
                placeholder="Institution name" className="w-full h-9 px-3 rounded-md bg-white border hairline text-[13px] focus:outline-none focus:border-neutral-900" />
            </div>
            <div className="col-span-3">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12.5px] text-neutral-400 display">$</span>
                <BalanceInput
                  value={a.balance}
                  onChange={(v) => update(i, 'balance', v)}
                />
              </div>
            </div>
            <div className="col-span-1 flex justify-end">
              <button onClick={() => remove(i)} className="h-8 w-8 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-xl border hairline bg-[hsl(var(--kf-gold-soft))]/50 px-5 py-4 flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-[hsl(var(--kf-gold-deep))] font-bold">Total assets</div>
        <div className="display text-[22px] font-bold text-neutral-900 tracking-tight">{money(totalAssets)}</div>
      </div>
    </div>
  );
}

/**
 * BalanceInput — small inline money input shaped like the row cell.
 * Formats with thousand commas on blur, raw digits while focused, uses the
 * Bricolage Grotesque "display" font for visual consistency with the rest of
 * the form's number fields.
 */
function BalanceInput({ value, onChange }) {
  const [focused, setFocused] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const display = (() => {
    if (focused) return draft ?? String(value || '');
    if (!value && value !== 0) return '';
    const n = Number(value);
    if (!Number.isFinite(n)) return String(value);
    if (n === 0) return '';
    return Number.isInteger(n)
      ? n.toLocaleString('en-US')
      : n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  })();
  return (
    <input
      type="text"
      inputMode="decimal"
      value={display}
      onFocus={() => { setFocused(true); setDraft(value ? String(value) : ''); }}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9.\-]/g, '');
        setDraft(raw);
        const n = raw === '' || raw === '-' || raw === '.' || raw === '-.' ? 0 : Number(raw);
        if (!Number.isNaN(n)) onChange(n);
      }}
      onBlur={() => { setFocused(false); setDraft(null); }}
      className="w-full h-9 pl-7 pr-3 rounded-md bg-white border hairline text-[13px] display tracking-tight font-semibold text-neutral-900 focus:outline-none focus:border-neutral-900"
    />
  );
}

/* =============== Property ============== */

export function PropertySection({ data, set, setMany, flushSave }) {
  // "Same as current address" copies the borrower's currentAddress into the
  // subject-property block. Only relevant for refinance scenarios where the
  // borrower is refinancing the home they live in.
  const isRefi = (data.loanPurpose || '').toLowerCase().includes('refi') ||
                 (data.loanPurpose || '').toLowerCase().includes('heloc') ||
                 (data.loanPurpose || '').toLowerCase().includes('home equity');

  const sameAsCurrent = !!data.currentAddress &&
    data.propertyAddress === data.currentAddress &&
    data.propertyCity === data.currentCity &&
    data.propertyState === data.currentState &&
    data.propertyZip === data.currentZip;

  const applyCurrentAddress = () => {
    const patch = {
      propertyAddress: data.currentAddress || '',
      propertyUnit: data.currentUnit || '',
      propertyCity: data.currentCity || '',
      propertyState: data.currentState || '',
      propertyCounty: data.currentCounty || '',
      propertyZip: data.currentZip || '',
    };
    if (typeof setMany === 'function') {
      setMany(patch);
    } else {
      Object.entries(patch).forEach(([k, v]) => set(k, v));
    }
    // Toggles are one-shot, explicit actions — bypass the 800ms debounce
    // so a refresh/close right after the click doesn't drop the write.
    if (typeof flushSave === 'function') {
      setTimeout(() => flushSave(), 0);
    }
  };

  const clearPropertyAddress = () => {
    const patch = {
      propertyAddress: '',
      propertyUnit: '',
      propertyCity: '',
      propertyState: '',
      propertyCounty: '',
      propertyZip: '',
    };
    if (typeof setMany === 'function') {
      setMany(patch);
    } else {
      Object.entries(patch).forEach(([k, v]) => set(k, v));
    }
    if (typeof flushSave === 'function') {
      setTimeout(() => flushSave(), 0);
    }
  };

  const setEdit = (field, value) => {
    set(field, value);
  };

  const hasCurrentAddress = !!(data.currentAddress || data.currentCity || data.currentZip);

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-4 pb-2 border-b hairline">
          <div className="text-[14px] font-bold text-neutral-900 dark:text-neutral-100">Subject property</div>
          {hasCurrentAddress && (
            <button
              type="button"
              data-testid="property-same-as-current"
              onClick={() => (sameAsCurrent ? clearPropertyAddress() : applyCurrentAddress())}
              title={isRefi ? 'Common for refinances — copies the borrower\'s current home address' : 'Copy the borrower\'s current address into the subject property'}
              className={cn(
                'flex items-center gap-2 h-8 px-3 rounded-md text-[11.5px] font-semibold transition',
                sameAsCurrent
                  ? 'bg-[hsl(var(--kf-gold))] text-neutral-900 ring-1 ring-[hsl(var(--kf-gold-deep))]/40 shadow-[0_0_0_3px_hsl(var(--kf-gold))/0.15]'
                  : 'border hairline bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 hover:border-[hsl(var(--kf-gold))] hover:text-[hsl(var(--kf-gold-deep))]'
              )}
            >
              <span className={cn('inline-block h-3 w-3 rounded-full border-2',
                sameAsCurrent ? 'border-neutral-900 bg-white' : 'border-neutral-300 dark:border-neutral-600')} />
              Same as current address
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <div className="md:col-span-3">
            <AddressAutocomplete
              label="Property address"
              required
              testId="property-address"
              value={data.propertyAddress}
              onChange={(v) => setEdit('propertyAddress', v)}
              onSelect={(d) => {
                setEdit('propertyAddress', d.street || data.propertyAddress);
                setEdit('propertyCity', d.city || '');
                setEdit('propertyState', d.state_code || '');
                setEdit('propertyCounty', d.county || '');
                setEdit('propertyZip', d.zip || '');
              }}
            />
          </div>
          <Field label="Unit / Apt" value={data.propertyUnit} onChange={(v) => setEdit('propertyUnit', v)} />
          <Field label="City" value={data.propertyCity} onChange={(v) => setEdit('propertyCity', v)} required />
          <Field label="State" value={data.propertyState} onChange={(v) => setEdit('propertyState', (v || '').toUpperCase().slice(0, 2))} mono />
          <Field label="ZIP" value={data.propertyZip} onChange={(v) => setEdit('propertyZip', v)} mono />
          <Field label="County" value={data.propertyCounty} onChange={(v) => setEdit('propertyCounty', v)} />
          <Select label="Property type" value={data.propertyType} onChange={(v) => set('propertyType', v)} options={['Single Family Residence (1 Unit)','Townhouse','Condo','2-4 Unit','Manufactured','PUD']} />
          <Select label="Occupancy" value={data.occupancy} onChange={(v) => set('occupancy', v)} options={['Primary Residence','Second Home','Investment / Rental']} />
        </div>
      </div>

      <Group title="Valuation">
        <Field label="Estimated value" type="number" value={data.propertyValue} onChange={(v) => set('propertyValue', Number(v))} prefix="$" mono required />
        <Field label="Purchase price" type="number" value={data.purchasePrice} onChange={(v) => set('purchasePrice', Number(v))} prefix="$" mono />
      </Group>

      <Group title="Annual costs">
        <Field label="Property taxes / yr" type="number" value={data.taxes} onChange={(v) => set('taxes', Number(v))} prefix="$" mono />
        <Field label="Hazard insurance / yr" type="number" value={data.insurance} onChange={(v) => set('insurance', Number(v))} prefix="$" mono />
        <Field label="HOA / mo" type="number" value={data.hoa} onChange={(v) => set('hoa', Number(v))} prefix="$" mono />
      </Group>
    </div>
  );
}

/* =============== Loan terms ============== */

export function LoanSection({ data, set }) {
  // Auto-default refinanceType to NoCashOut the moment the LO selects
  // "Refinance" as the loan purpose. ARIVE requires refinanceType whenever
  // loanPurpose is Refinance, so we never want this field to be blank.
  // The LO can still change it (Limited Cash Out / Cash Out) before pushing.
  const handleLoanPurposeChange = (v) => {
    set('loanPurpose', v);
    if (v === 'Refinance' && !data.refinanceType) {
      set('refinanceType', 'NoCashOut');
    }
    if (v === 'Purchase') {
      // Clear refinanceType + cashoutPurpose on Purchase so ARIVE doesn't
      // see stale values that don't apply to a purchase loan.
      set('refinanceType', '');
      set('cashoutPurpose', '');
    }
  };
  // When the LO switches to CashOut, auto-default cashoutPurpose to
  // DebtConsolidation (ARIVE requires it). Clear when leaving CashOut so
  // the field doesn't linger in the DB on No Cash Out / Limited refis.
  const handleRefinanceTypeChange = (v) => {
    set('refinanceType', v);
    if (v === 'CashOut' && !data.cashoutPurpose) {
      set('cashoutPurpose', 'DebtConsolidation');
    }
    if (v !== 'CashOut') {
      set('cashoutPurpose', '');
    }
  };
  return (
    <div className="space-y-6">
      <Group title="Loan request">
        <Select label="Loan purpose" value={data.loanPurpose} onChange={handleLoanPurposeChange} options={['Purchase','Refinance']} />
        {data.loanPurpose === 'Refinance' && (
          <Select
            label="Refinance type"
            value={data.refinanceType || 'NoCashOut'}
            onChange={handleRefinanceTypeChange}
            options={[
              { v: 'NoCashOut', l: 'No Cash Out (Rate & Term)' },
              { v: 'LimitedCashOut', l: 'Limited Cash Out' },
              { v: 'CashOut', l: 'Cash Out' },
            ]}
          />
        )}
        {data.loanPurpose === 'Refinance' && data.refinanceType === 'CashOut' && (
          <Select
            label="Cash-out purpose"
            value={data.cashoutPurpose || 'DebtConsolidation'}
            onChange={(v) => set('cashoutPurpose', v)}
            options={[
              { v: 'DebtConsolidation',     l: 'Debt Consolidation' },
              { v: 'HomeImprovement',       l: 'Home Improvement' },
              { v: 'Education',             l: 'Education' },
              { v: 'InterestRateReduction', l: 'Interest Rate Reduction' },
              { v: 'Other',                 l: 'Other' },
            ]}
          />
        )}
        <Select label="Loan type" value={data.loanType} onChange={(v) => set('loanType', v)} options={['Conventional','FHA','VA','USDA','Non-QM','HELOC']} />
        <Field label="Loan amount" type="number" value={data.loanAmount} onChange={(v) => set('loanAmount', Number(v))} prefix="$" mono required />
        <Select label="Term" value={data.loanTerm} onChange={(v) => set('loanTerm', Number(v))} options={[{ v: 360, l: '30 yr' }, { v: 240, l: '20 yr' }, { v: 180, l: '15 yr' }, { v: 120, l: '10 yr' }]} />
        <Field label="Rate" type="number" step="0.001" value={data.rate} onChange={(v) => set('rate', Number(v))} suffix="%" mono required />
        <Select label="Lock status" value={data.lockStatus} onChange={(v) => set('lockStatus', v)} options={['Floating','Locked','Lock requested']} />
        {data.lockStatus === 'Locked' && (
          <Field label="Lock expiration" mask="date" value={data.lockExpiration} onChange={(v) => set('lockExpiration', v)} placeholder="MM/DD/YYYY" />
        )}
        <Field label="Lender" value={data.lender} onChange={(v) => set('lender', v)} placeholder="e.g. UWM, Rocket Pro, Kind Lending" />
        <Field label="Loan date" mask="date" value={data.loanDate} onChange={(v) => set('loanDate', v)} placeholder="MM/DD/YYYY" />
      </Group>
    </div>
  );
}

/* =============== Declarations ============== */

export function DeclarationsSection({ data, set }) {
  const items = [
    { key: 'bankruptcy', q: 'Have you declared bankruptcy in the last 7 years?' },
    { key: 'foreclosure', q: 'Have you had a property foreclosed in the last 7 years?' },
    { key: 'lawsuit', q: 'Are you a party to a lawsuit?' },
    { key: 'federalDelinquent', q: 'Are you delinquent on any federal debt?' },
    { key: 'alimony', q: 'Are you obligated to pay alimony or child support?' },
    { key: 'coSigner', q: 'Are you a co-signer or endorser on any other debt?' },
    { key: 'primaryResidence', q: 'Will this property be your primary residence?' },
  ];
  const setDecl = (k, v) => set('declarations', { ...data.declarations, [k]: v });
  return (
    <div className="space-y-2">
      <h3 className="display text-[16px] font-bold text-neutral-900 tracking-tight mb-2">Borrower declarations</h3>
      {items.map((it) => (
        <div key={it.key} className="flex items-center justify-between rounded-lg border hairline px-4 py-3">
          <span className="text-[13px] text-neutral-800 max-w-[80%]">{it.q}</span>
          <div className="flex items-center gap-1">
            <Toggle active={data.declarations?.[it.key] === true} label="Yes" onClick={() => setDecl(it.key, true)} />
            <Toggle active={data.declarations?.[it.key] === false} label="No" onClick={() => setDecl(it.key, false)} />
          </div>
        </div>
      ))}
    </div>
  );
}
